import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef
} from 'react';
import { useMount } from 'react-use';

interface RegisterFieldParams {
  label?: ReactNode;
}

interface FieldContext {
  register(name: string, params: RegisterFieldParams): void;
  getParams(name: string): RegisterFieldParams;
}

const RegisterFieldContext = createContext<FieldContext>(null!);

export function useRegisterField(name: string, params: RegisterFieldParams) {
  const { register } = useContext(RegisterFieldContext);
  useMount(() => register(name, params));
}

export function useFieldParams() {
  const { getParams } = useContext(RegisterFieldContext);
  return useCallback((name: string) => getParams(name), [getParams]);
}

export function RegisterFieldProvider({ children }: { children: ReactNode }) {
  const fields = useRef<Record<string, RegisterFieldParams>>({});

  const register = useCallback((name: string, params: RegisterFieldParams) => {
    fields.current[name] = params;
  }, []);

  const getParams = useCallback((name: string) => fields.current[name], []);

  const value = useMemo(() => ({ register, getParams }), [getParams, register]);
  return (
    <RegisterFieldContext.Provider value={value}>
      {children}
    </RegisterFieldContext.Provider>
  );
}
